import { PolicyCollectiveLife } from '@models/policy-collective-life';
import { PolicyConfig } from '@models/policy-config';
import { ClientAreaCardConfig } from '@models/products';
import { Address } from './address';
import { PaymentPlan } from './payment-plan';
import { PolicyAccident } from './policy-accident';
import { PolicyCar } from './policy-car';
import { PolicyDeath } from './policy-death';
import { PolicyHealth, PolicyHealthInsured } from './policy-health';
import { PolicyHome } from './policy-home';
import { PolicyInvestment } from './policy-investment';
import { PolicyInvestmentNova } from './policy-investment-nova';
import { PolicyLife } from './policy-life';
import { PolicyOther } from './policy-other';
import { PolicyPension } from './policy-pension';
import { PolicySaving } from './policy-saving';

export type PolicyBaseType =
  | 'car'
  | 'home'
  | 'life'
  | 'collective-life'
  | 'saving'
  | 'health'
  | 'pension'
  | 'investment'
  | 'investment-nova'
  | 'other'
  | 'accident'
  | 'death';

export type State = 'PENDING' | 'LOADING' | 'READY' | 'ERROR';

export const POLICY_BASE_TYPES: PolicyBaseType[] = [
  'car',
  'home',
  'life',
  'collective-life',
  'saving',
  'health',
  'pension',
  'investment',
  'investment-nova',
  'other',
  'accident',
  'death'
];

export type PolicyReportDocumentType =
  | 'AUD'
  | 'QUA'
  | 'DUP'
  | 'CER'
  | 'BROCHURE';

export type PolicyMedicalManagementType =
  | 'AUTHORIZATION'
  | 'ASSISTANCE'
  | 'REFUND';

export type Policy =
  | PolicyCar
  | PolicyHome
  | PolicyOther
  | PolicyPension
  | PolicyInvestment
  | PolicyInvestmentNova
  | PolicyLife
  | PolicyCollectiveLife
  | PolicySaving
  | PolicyHealth
  | PolicyDeath
  | PolicyAccident;

export function getPolicyWithFunds(p: Policy) {
  return p as PolicyPension | PolicyInvestment | PolicySaving;
}

export interface PolicyBase {
  policyNumber: string;
  alias: string;
  paymentPlans?: PaymentPlan[];
  isPolicyInPaymentPlan?: boolean;
  policyBaseType?: string;
  configuration?: PolicyConfig;
  clientAreaCardConfig?: ClientAreaCardConfig;
  pendingReceipts: boolean;
  policyDisabled?: boolean;
}

export interface PolicyReceipt {
  number: string;
  status: string;
  effectDate: string;
  amount: string;
  next: boolean;
  last: boolean;
  netBonus: string;
  ptcAmount: string;
  taxes: string;
  paid: boolean;
  pendingOfPayment: boolean;
  situationType: string;
  situationCode: string;
  bonus: string;
  businessDiscount?: string;
  interests?: string;
  duplicateDownloadable?: boolean;

  /* HomeReceipt attrs */
  surcharge: string;

  /* LifeReceipt attrs */
  coverageExpirationDate: string;
  riskSurcharge: string;
  liquidationSurcharge: string;
  pageReceipt: string;
}

export interface PolicySinister {
  code: string;
  date: string;
  openDate: string;
  description: string;
  status: string;
  active: boolean;
  last: boolean;
  savedReportId: string;

  /* CarSinister attrs */
  expedientId: string;
  statusDetail: string;
  hour: string;
  existsInjureds: string;
  existsThirdPartyInvolved: string;
  address: Address;
  expertiseAllowed?: boolean;
  hasExpertiseAvailable?: boolean;
  hasWorkshopAvailable?: boolean;
  canCirculate?: boolean;
  hasServiceCenter?: boolean;
  hasDoorToDoor?: boolean;
  disappearedVehicle?: boolean;
  hasReplacement?: boolean;
  sinisterType?: string;
  serviceRequestId?: string;

  /* Home/Other Sinister attrs */
  informer: string;
  phone: string;
  mobilePhone: string;
  finished: boolean;
  requestReference: string;

  /* DraftCarSinister attrs */
  intermediateExpedientId?: string;
}
export interface PolicyFund {
  warranties: PolicyFundWarranty[];
  status: string;
  policyNumber: string;
  valueDate: string;
  liquidationValue: string;
  currentBalance: string;
  currentNet: string;
  previousYearNet: string;
  productId?: string;
}

export interface PolicyFundWarranty {
  description: string;
  capital: number;
  netAmount: number;
}

export interface PolicyMedicalAssistance {
  medicalManagementType: 'ASSISTANCE';
  date: string;
  description: string;
  speciality: string;
  center: string;
}

export interface PolicyMedicalAuthorization {
  medicalManagementType: 'AUTHORIZATION';
  date: string;
  description: string;
  status: string;
  number: string;
  facultative: string;
  insured?: PolicyHealthInsured;
}

export interface PolicyCopaymentCard {
  date: string;
  id: string;
}

export interface PolicyCopaymentCorresponse {
  checked: boolean;
}

export interface PolicyExpedient {
  number: string;
  type: string;
}

export interface Document {
  name?: string;
  mimeType?: string;
  base64Content: string;
  size?: number;
  type?: string;
}

export interface PolicyParticipant {
  participantType: ParticipantType;
  documentNumber: string;
  residence: string;
  phone: string;
  name?: string;
  birthdate?: string;
  socialReason?: string;
  email?: string;
}

export type ParticipantType = 'HOLDER' | 'PAYER' | 'REPRESENTATIVE' | 'INSURED';

export enum TrimestralReportErrorCodes {
  PEN_INV_DOCUMENT_GENERIC = 'PEN_INV_DOCUMENT_GENERIC',
  CLIENTS_AREA_PENSION_DOCUMENT_NOT_AVAILABLE = 'CLIENTS_AREA.PENSION_DOCUMENT.NOT_AVAILABLE'
}

export const PolicyTypeInUrlPath = {
  car: 'carPolicies',
  home: 'homeOtherPolicies',
  other: 'homeOtherPolicies',
  accident: 'homeOtherPolicies',
  pension: 'pensionInvestmentPolicies',
  life: 'lifePolicies',
  saving: 'lifePolicies',
  health: 'healthPolicies',
  death: 'homeOtherPolicies',
  investment: 'pensionInvestmentPolicies',
  investmentNova: 'pensionInvestmentPolicies'
} as const;


export function checkPolicyHasDetail(policy: Policy) {
  const hasPolicyConfig = policy?.configuration?.cardConfiguration?.consultations?.policyDetail;
  if (hasPolicyConfig !== undefined) {
    return !!hasPolicyConfig;
  }

  if (!!policy && policy.policyDisabled) {
    return false;
  }
  return true;
}

